import {
  AddButton,
  PageSpinner,
  FuturePlanUpdate,
  OldFilterButton,
  OldFilterButtonGroup
} from '@td-client-frontend/common';
import { FuturePlanUpdateCCM } from '../../../components/form-controls/FuturePlanUpdateCcm';
import { ExportIcon, ViewFile } from '@td-client-frontend/icons';

import * as _ from 'lodash';
import moment, { Moment } from 'moment';
import React, { Suspense, useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';

import { FormData, GroupType } from '../../../api/api';
import Search from '../../../components/EmployerGroupSearch/Search_old';
import { useModal } from '../../../components/Modal';
import Modal from '../../../components/Modal/NewModal';
import { SideMenu } from '../../../components/SideMenu_old';
import { allowedPublishField, chronicCareProductsDefaults } from '../../../constants/formConstants';
import { useAxios } from '../../../context/AxiosContext';
import { FormRendererWithHandlers } from '../../../context/ddf-context';
import { useSchema } from '../../../hooks/useSchema';
import {
  combineFieldArrays,
  omitExtraFields,
  transformGroups,
  transformRecursively
} from '../../../utils/old_outcomingDataTransformationHelper';
import { NewGroupModalContent } from './NewGroupModalContent';
import {
  ViewPlanNumbersArgs,
  ViewPlanNumbersModal,
  ViewPlanNumbersOption,
  ViewPlanNumbersState
} from './ViewPlanNumbersModal';
import { downloadFileFromResponse } from '../../../utils/fileDownload';
import { TermModal } from './TermModal';
import WithPermission, { PermMode } from '../../../components/WithPermission';
import useGroupList from '../../../hooks/old_useGroupList';
import { addTrackEvent } from '../../../utils/piwikUtils';
import { PiwikName } from '../../../@types/common';
import List, { NoResultsDialog } from '../../../components/List';
import { setLogo } from '../../../hooks/useFormData';
import { useHistory } from 'react-router-dom';
import { v4 } from 'uuid';
import { useAuth } from '../../../context/AuthContext';
import { getGroupNumber, hasProductChange } from '../../../utils/groups/helpers';

const getDefaultDraftsName = () => `group_drafts_${moment.utc().format('MMDDYYYY')}.csv`;

const EmployerGroups = () => {
  const headers = ['Employers', 'Group Number'];
  const addInHeader = useRef(null);
  const { authAxios } = useAxios();
  const { showModal, hideModal, refreshModalWith } = useModal();
  const queryCache = useQueryClient();
  const [showGroupModal, setShowGroupModal] = useState<boolean>(false);
  const [searchString, setSearchString] = useState('');
  const loadMoreRef = React.useRef<HTMLSpanElement>(null);
  const history = useHistory();
  const [showViewPlanNumbersModal, setShowViewPlanNumbersModal] = useState<boolean>(false);
  const [viewPlanNumbersState, setViewPlanNumbersState] = useState<ViewPlanNumbersState | null>(null);

  const { checkPerm } = useAuth();

  const onShowViewPlanNumbersClose = () => {
    setShowViewPlanNumbersModal(false);
    setViewPlanNumbersState(null);
  };

  const onViewPlanNumbers = ({ updateOptions, codeOptions, initialCodeOptionsState }: ViewPlanNumbersArgs) => {
    const viewPlanNumbersOptions: ViewPlanNumbersOption[] = codeOptions.length
      ? codeOptions.map((codeOption) => {
          return {
            ...codeOption,
            id: v4(),
            isPlainText: !!(
              initialCodeOptionsState.length &&
              initialCodeOptionsState.some((initilaCodeOption) => initilaCodeOption.code === codeOption.code) &&
              ['active', 'inactive'].includes(filter)
            )
          };
        })
      : [
          {
            classCodes: [],
            bpCodes: [],
            value: '',
            label: '',
            code: '',
            id: v4()
          }
        ];
    setViewPlanNumbersState({ updateOptions, viewPlanNumbersOptions });
    setShowViewPlanNumbersModal(true);
  };

  const { listItems, status, filter, onFilterChange, form, canFetchMore, isFetchingMore } = useGroupList({
    search: searchString,
    loadMoreRef
  });

  const { schema } = useSchema({ form: 'groups' });

  const submitData = useCallback(
    async (data) => {
      const { _id, ...updateData } = data;
      addTrackEvent(PiwikName.PUBLISH);

      const combinedArrays = combineFieldArrays(updateData);
      const transformedRowsToArrayData = transformRecursively(combinedArrays);
      transformGroups(transformedRowsToArrayData);
      const preparedData = omitExtraFields(transformedRowsToArrayData, allowedPublishField);
      const isCreate = filter === 'draft';
      const logo = await setLogo(data.logo);
      if ((filter === 'draft' && logo.action !== 'KEEP') || filter !== 'draft') {
        preparedData.logo = logo;
      }
      const response = { data: {} };
      await authAxios.post<FormData>('/publish', { ...preparedData, id: _id, isCreate });

      return response.data;
    },
    [authAxios, filter]
  );

  const publishMutation = useMutation(submitData, {
    onSuccess: (data, variables) => {
      const groupType = filter === 'draft' ? 'drafts' : 'groups';
      queryCache.invalidateQueries('form');
      queryCache.invalidateQueries(groupType);
      addTrackEvent(PiwikName.PUBLISH_SUCCESS);
      showModal({
        size: 'prompt',
        header: 'Your groups have been submitted for processing.',
        content: (
          <section className="p-6 font-bold">
            <p>Please allow up to 1 business day for activation.</p>
          </section>
        ),
        actions: [{ title: 'OK', kind: 'primary' }],
        returnFocusAfterCloseTo: 'delete-draft-footer-button'
      });
    },
    onError: () => {
      addTrackEvent(PiwikName.PUBLISH_FAIL);
      toast.error('Unable to publish changes, please try again');
    }
  });

  const publishData = useCallback(
    async (data) => {
      showModal({
        size: 'prompt',
        header: 'Are you sure you want to continue?',
        content: (
          <section className="p-6 font-bold">
            <p>You are about to publish changes to {data?.name || 'this group.'}</p>
          </section>
        ),
        actions: [
          { title: 'Cancel', kind: 'secondary' },
          { title: 'Continue', kind: 'primary', onClick: () => publishMutation.mutate(data) }
        ]
      });
    },
    [publishMutation, showModal]
  );

  const deleteDraft = useCallback(
    (data) => {
      addTrackEvent(PiwikName.DELETE_DRAFT);
      showModal({
        size: 'prompt',
        header: 'Delete a draft of the group',
        alertIcon: true,
        content: (
          <section className="p-6 font-bold font-effra">
            <p>You are about to delete {data.name || 'this'} draft</p>
            <p>are you sure you'd like to proceed and delete this group?</p>
          </section>
        ),
        actions: [
          { title: 'Cancel', kind: 'secondary' },
          {
            title: 'Yes, delete this group',
            kind: 'primary',
            onClick: () => {
              form.removeFormData({ type: filter, id: data._id });
              // invalidate query cache? no idea how to fix this issue
            }
          }
        ],
        returnFocusAfterCloseTo: 'delete-draft-footer-button'
      });
    },
    [showModal, form, filter]
  );

  const terminateGroup = useCallback(
    (data) => {
      let benefitGroups = data.groups || [];
      let termDate: Moment | null = data.end_dt ? moment(new Date(data.end_dt)).utc().startOf('day') : null;
      let terminateReason = '';

      const isDisabled = () => {
        const isDateValid = moment().diff(termDate, 'days') <= 60;
        return !(isDateValid && terminateReason);
      };

      const actions = [
        { title: 'Cancel', kind: 'secondary' },
        {
          title: 'Yes, terminate this group',
          kind: 'primary',
          isDisabled,
          onClick: () => {
            benefitGroups = benefitGroups.map((group: { end_dt: moment.MomentInput }) => {
              if (
                !group.end_dt ||
                (moment(group.end_dt).isValid() && moment(group.end_dt).utc().startOf('day').diff(termDate) >= 60)
              ) {
                return { ...group, end_dt: termDate?.format('YYYY-MM-DD') };
              }
              return group;
            });

            const newValues = {
              ...data,
              groups: benefitGroups || null,
              end_dt: termDate?.format('YYYY-MM-DD'),
              terminateReason
            };

            setTimeout(() => terminateConfirmationModal(newValues), 500);
          }
        }
      ];

      const terminateConfirmationModal = (values: any) => {
        const { groups, end_dt } = values;
        const benefitGroupName = !groups.length ? groups[0].name : '[Benefit group]';
        return showModal({
          size: 'prompt',
          alertIcon: true,
          header: 'Terminate this group',
          content: (
            <section className="p-6">
              <h3 className="pt-3 pb-3 font-bold">Are you sure you want to terminate this group?</h3>
              <p>
                You are about to cancel {benefitGroupName} and all of its associated groups on {end_dt}
              </p>
            </section>
          ),
          actions: [
            { title: 'No, return to group', kind: 'secondary' },
            {
              title: 'Yes, terminate group',
              kind: 'primary',
              onClick: async () => publishMutation.mutate(values)
            }
          ]
        });
      };

      showModal({
        header: 'Terminate this group',
        content: (
          <TermModal
            initialValue={data.end_dt}
            selectedGroup={data}
            // onChange={(value: string) => (termDate = value ? moment(value).utc().startOf('day') : null)}
            onDateChange={(value: string) => {
              // formattedDate = value;
              termDate = moment(value).utc().startOf('day');
              refreshModalWith({ actions });
            }}
            onReasonChange={(value: string) => {
              terminateReason = value;
              refreshModalWith({ actions });
            }}
          />
        ),
        actions,
        returnFocusAfterCloseTo: 'terminate-group-footer-button'
      });
    },
    [publishMutation, showModal, refreshModalWith]
  );
  // New Group Data Setters
  const [newGroupNumber, setNewGroupNumber] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [hasError, setHasError] = useState(false);

  const toggleNewGroupModal = () => {
    setShowGroupModal(!showGroupModal);
    setNewGroupName('');
    setNewGroupNumber('');
  };

  const onNewGroupSave = () => {
    if (!newGroupName || !newGroupNumber) {
      setHasError(true);
      return;
    }
    setHasError(false);
    const newGroupData = {
      'contacts--Reporters': [null],
      'name': newGroupName,
      'planNumber': newGroupNumber
    };
    form.insertFormData({ data: newGroupData, type: 'draft' });
    addTrackEvent(PiwikName.NEW_GROUP);
    toggleNewGroupModal();
    onFilterChange(GroupType.DRAFT);
  };

  const handleChange = (type: string, value: string) => {
    if (type === 'NAME') {
      setNewGroupName(value);
    } else {
      setNewGroupNumber(value);
    }
  };

  const exportDrafts = useCallback(async () => {
    await authAxios.get('/draft/export').then((response) => {
      downloadFileFromResponse(response, getDefaultDraftsName());
    });
  }, [authAxios]);
  const productState = (code: string, suffix: string) => `products--${code}--${suffix}`;

  const handleChronicCareRadio = ({ name, value, getState, change }: any) => {
    const groupNumber = getGroupNumber(name);
    const state = getState().values;
    if (isFinite(groupNumber) && state.groups && state.groups[groupNumber]) {
      const formData = form?.formData?.groups?.[groupNumber];
      const productCodesToProcess = Object.keys(chronicCareProductsDefaults);

      change(`groups[${groupNumber}]`, {
        ...state.groups[groupNumber],
        ...Object.fromEntries(
          productCodesToProcess.flatMap((code) => {
            const currentProductDefaultDefinition = chronicCareProductsDefaults[code];

            return Object.entries(currentProductDefaultDefinition).map(([suffix, defaultValue]) => {
              const key = productState(code, suffix);
              return [key, formData?.[key] ?? defaultValue];
            });
          })
        ),
        [productState('chronicCare', 'start_dt')]: formData?.[productState('chronicCare', 'start_dt')] ?? null,
        [productState('chronicCare', 'end_dt')]: formData?.[productState('chronicCare', 'end_dt')] ?? null,
        [productState('chronicCare', 'isEnabled')]: formData?.[productState('chronicCare', 'isEnabled')] ?? false
      });
    }
  };

  const handleViewActivityLogClick = () => {
    history.push(`/activity-log?employerName=${form.formData.name}`);
  };

  const listLoading = status === 'loading';

  const overridesDeleteButton = checkPerm(['DraftWrite', 'DraftCreate'], PermMode.ANY)
    ? {
        groups: {
          left: [
            {
              name: 'left',
              title: 'Delete this Plan',
              action: 'deletePlan',
              displayOnTypes: ['draft']
            }
          ]
        }
      }
    : {};

  return (
    <div>
      {viewPlanNumbersState?.viewPlanNumbersOptions && (
        <ViewPlanNumbersModal
          isOpen={showViewPlanNumbersModal}
          onClose={onShowViewPlanNumbersClose}
          initialPlanNumbersOptions={viewPlanNumbersState?.viewPlanNumbersOptions}
          onChange={viewPlanNumbersState?.updateOptions}
        />
      )}
      <Modal
        isOpen={showGroupModal}
        onClose={toggleNewGroupModal}
        contentLabel="New Employer Group Modal"
        title="New Employer Group Modal"
        header="New Employer Group"
        size="medium"
        actions={[
          { title: 'Cancel', kind: 'secondary' },
          {
            title: 'Continue',
            kind: 'primary',
            autoClose: false,
            onClick: onNewGroupSave
          }
        ]}
      >
        <NewGroupModalContent
          handleChange={handleChange}
          setHasError={setHasError}
          hasError={hasError}
          newGroupName={newGroupName}
          newGroupNumber={newGroupNumber}
        />
      </Modal>

      <div className="flex items-center justify-between w-full px-6 py-4 pt-10 mx-auto bg-white sm:items-baseline">
        <h1 className="text-4xl font-effra">Employer Groups</h1>
        <div className="flex flex-row items-center space-x-8">
          {!!form?.formData?.name && (
            <WithPermission requiredPerms={['AuditRead']}>
              <AddButton onClick={handleViewActivityLogClick} label="View activity log" Icon={<ViewFile />} />
            </WithPermission>
          )}
          <AddButton onClick={toggleNewGroupModal} label="New group" ref={addInHeader} />
          {filter === 'draft' && (
            <WithPermission requiredPerms={['DraftRead']} mode={PermMode.ANY}>
              <button onClick={exportDrafts} className="flex items-center space-x-2 btn btn-default">
                <ExportIcon aria-hidden />
                <p className="text-primary-500">Export all available drafts</p>
              </button>
            </WithPermission>
          )}
        </div>
      </div>
      <div
        className="flex items-stretch flex-none flex-grow min-h-screen overflow-x-none"
        style={{ minHeight: '50rem' }}
      >
        {filter && (
          <SideMenu>
            <OldFilterButtonGroup>
              <WithPermission requiredPerms={['GroupRead']} mode={PermMode.ANY}>
                <>
                  <OldFilterButton
                    label="Active"
                    onClick={() => onFilterChange(GroupType.ACTIVE)}
                    isSelected={filter === 'active'}
                    disabled={listLoading}
                  />
                  <OldFilterButton
                    label="Inactive"
                    onClick={() => onFilterChange(GroupType.INACTIVE)}
                    isSelected={filter === 'inactive'}
                    disabled={listLoading}
                  />
                </>
              </WithPermission>
              <WithPermission requiredPerms={['DraftRead']} mode={PermMode.ANY}>
                <OldFilterButton
                  label="Drafts"
                  onClick={() => onFilterChange(GroupType.DRAFT)}
                  isSelected={filter === 'draft'}
                  disabled={listLoading}
                />
              </WithPermission>
            </OldFilterButtonGroup>
            <Search onSearch={setSearchString} loading={listLoading} />
            <List title="Groups search result table" headers={headers} loading={listLoading}>
              {listItems}
              {listItems?.length > 0 && filter === 'draft' && (
                <tr>
                  <td colSpan={headers.length || 1} className="py-4 text-center font-effra">
                    <span ref={loadMoreRef} className="my-4">
                      {isFetchingMore ? 'Loading more...' : canFetchMore ? 'Load More' : 'Nothing more to load'}
                    </span>
                  </td>
                </tr>
              )}
              {!listItems?.length && (
                <NoResultsDialog
                  headers={headers}
                  newEntryLabel="New group"
                  noResultAction={toggleNewGroupModal}
                  noResultsLabel="No results found. Please try another search or add a new group"
                  permissionsForButton={['GroupCreate', 'DraftCreate']}
                />
              )}
            </List>
          </SideMenu>
        )}
        {form.formStatus !== 'error' && (
          <FormRendererWithHandlers
            key={`${form.formData?._id ?? 'none'}-${form.formDataUpdatedAt ?? 0}`}
            loading={form.formStatus === 'loading'}
            schema={schema}
            groupType={filter}
            onSubmit={() => {}}
            initialValues={form.formData}
            footerOverrides={{ ...overridesDeleteButton }}
            handlersMapper={{
              viewPlanNumbers: onViewPlanNumbers,
              save: form.updateFormData,
              publish: publishData,
              deleteDraft,
              handleChronicCareRadio,
              deletePlan: (state, change, setSubForm) => {
                const {
                  __footerOverride: { name, fieldName }
                } = state || {};
                if (name && fieldName) {
                  if (filter === 'draft') {
                    change(fieldName, undefined);
                    setSubForm('');
                  } else {
                    change(`${fieldName}.end_dt`, moment().format('yyyy-MM-DD'));
                  }
                }
              },
              cancel: (initialValues, change, setSubForm, reset) => {
                showModal({
                  header: 'Discard Employer Group changes?',
                  content: (
                    <section className="p-6 font-bold">
                      <p>You are about to discard changes to Employer Group.</p>
                      <p>are you sure you'd like to proceed?</p>
                    </section>
                  ),
                  actions: [{ title: 'Cancel' }, { title: 'Yes, discard changes', kind: 'primary', onClick: reset }],
                  returnFocusAfterCloseTo: 'delete-draft-footer-button'
                });
              },
              openSchedulePriceUpdate: (state, change) => {
                const {
                  __footerOverride: { fieldName }
                } = state || {};
                if (fieldName) {
                  let changeList = _.get(state, fieldName);
                  showModal({
                    size: 'large',
                    header: 'Schedule price updates',
                    content: (
                      <FuturePlanUpdate
                        values={state}
                        baseFieldName={fieldName}
                        onFieldChange={(name, value) => {
                          changeList = {
                            ...changeList,
                            [name]: value
                          };
                        }}
                      />
                    ),
                    actions: [
                      { title: 'Cancel' },
                      {
                        title: 'Save',
                        kind: 'primary',
                        onClick: () => {
                          addTrackEvent(PiwikName.FUTURE_EFFECTIVE_PRODUCTS);
                          change(fieldName, changeList);
                          hideModal();
                          toast.success('Please click Update to submit your changes', {
                            position: 'top-center',
                            hideProgressBar: true,
                            pauseOnHover: true,
                            draggable: true
                          });
                        }
                      }
                    ]
                  });
                }
              },
              openSchedulePriceUpdateCCM: (state, change) => {
                const {
                  __footerOverride: { fieldName }
                } = state || {};
                if (_.size(fieldName) > 0) {
                  let changeList = _.get(state, fieldName);
                  const initialState = _.cloneDeep(state);

                  const ccmActions = [
                    { title: 'Cancel' },
                    {
                      isDisabled: () =>
                        !changeList['futureProductsCCM--CCM--start_dt'] ||
                        (state.groups[fieldName.substring(7, 8)]['futureProductsCCM--CCM--start_dt'] ===
                          changeList['futureProductsCCM--CCM--start_dt'] &&
                          !hasProductChange(state.groups[fieldName.substring(7, 8)], changeList)),
                      title: 'Save',
                      kind: 'primary',
                      onClick: () => {
                        if (changeList['futureProductsCCM--CCM--start_dt']) {
                          change(fieldName, changeList);
                          hideModal();
                          toast.success('Please click Publish to submit your changes', {
                            position: 'top-center',
                            hideProgressBar: true,
                            pauseOnHover: true,
                            draggable: true
                          });
                        } else {
                          toast.error('There was an error saving your start date for CCM', {
                            position: 'top-center',
                            hideProgressBar: true,
                            pauseOnHover: true,
                            draggable: true
                          });
                        }
                      }
                    }
                  ];

                  showModal({
                    header: 'Schedule Chronic Care Services update',
                    content: (
                      <FuturePlanUpdateCCM
                        values={state}
                        initialState={initialState}
                        baseFieldName={fieldName}
                        onFieldChange={(name, value) => {
                          changeList = {
                            ...changeList,
                            [name]: value
                          };
                          refreshModalWith(ccmActions);
                        }}
                        onListChange={(list) => {
                          changeList = list;
                          refreshModalWith(ccmActions);
                        }}
                      />
                    ),
                    actions: ccmActions
                  });
                }
              },
              terminateGroup
            }}
            readOnly={false}
          />
        )}
      </div>
    </div>
  );
};

const SuspenseWithLoader = (props: any) => {
  return (
    <Suspense fallback={<PageSpinner />}>
      <EmployerGroups {...props} />
    </Suspense>
  );
};

export default SuspenseWithLoader;
